// src/components/VideoVerificatie.js
//
// Poort voor de video-analyse-pagina's: eerst de toestemming, dan naast de gewone login een code per
// e-mail (backend: /api/video-analyses/verificatie). Zo komt iemand met een onbeheerd ingelogd account
// niet zomaar bij de opnames. De pagina eronder wordt pas gemonteerd na beide, zodat de eigen aanroepen
// van die pagina het token meesturen en er voor de toestemming niets wordt opgevraagd (de backend zou
// met een 403 antwoorden, en die leest de poort als verlopen toegang).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useDoorloopjes } from '../context/DoorloopjesContext';
import VideoAnalyseConsent from './VideoAnalyseConsent';
import {
  VERIFICATIE_VERLOPEN_EVENT,
  bewaarToken,
  geldigToken,
  verificatieTot,
} from '../utils/videoVerificatie';

const STRAPI_URL = process.env.REACT_APP_STRAPI_URL;

const REDENEN = {
  onjuist: 'De code klopt niet.',
  verlopen: 'De code is verlopen. Vraag een nieuwe code aan.',
  te_vaak: 'Te vaak een verkeerde code ingevoerd. Vraag een nieuwe code aan.',
};

function VideoVerificatie({ children }) {
  const { currentUser, giveVideoAnalysisConsent } = useDoorloopjes();
  const [geverifieerd, setGeverifieerd] = useState(() => Boolean(geldigToken()));
  const [naar, setNaar] = useState('');
  const [code, setCode] = useState('');
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState('');
  const [wachtSeconden, setWachtSeconden] = useState(0);
  // Verlopen in een open tabblad: het codescherm wacht op de knop en stuurt niets uit zichzelf.
  const [verlopen, setVerlopen] = useState(false);
  const aangevraagd = useRef(false);

  const nodig = Boolean(currentUser?.has_video_analysis && currentUser?.video_analysis_consent_at);

  // Verlopen token in een open tabblad: terug naar het codescherm, via de timer of via een 403 uit een
  // pagina. Beide gebeuren ook als niemand kijkt (een pagina die de status blijft opvragen, een tabblad
  // dat 's nachts openstaat), dus er gaat pas een code als iemand op de knop drukt. Zonder toestemming is
  // er nog geen code nodig en dus niets dat kan verlopen; na de toestemming gaat de code meteen.
  useEffect(() => {
    if (!nodig) return undefined;
    const terug = () => {
      aangevraagd.current = true;
      setNaar('');
      setFout('');
      setVerlopen(true);
      setGeverifieerd(false);
    };
    window.addEventListener(VERIFICATIE_VERLOPEN_EVENT, terug);
    let timer = null;
    if (geverifieerd) {
      const tot = verificatieTot();
      timer = setTimeout(terug, Math.max(0, (tot || 0) - Date.now()));
    }
    return () => {
      window.removeEventListener(VERIFICATIE_VERLOPEN_EVENT, terug);
      if (timer) clearTimeout(timer);
    };
  }, [nodig, geverifieerd]);

  const [eerderGestuurd, setEerderGestuurd] = useState(false);

  // alleenAlsNodig: bij het openen van de pagina een nog geldige code laten staan (die staat in de
  // mail); alleen de knop "Nieuwe code sturen" vraagt altijd een nieuwe.
  const vraagCode = useCallback(async (alleenAlsNodig = false) => {
    setBezig(true);
    setFout('');
    setVerlopen(false);
    try {
      const jwt = localStorage.getItem('jwt');
      const res = await axios.post(
        `${STRAPI_URL}/api/video-analyses/verificatie/code`,
        { alleen_als_nodig: alleenAlsNodig },
        { headers: { Authorization: `Bearer ${jwt}` } }
      );
      setNaar(res.data?.data?.naar || '');
      setEerderGestuurd(Boolean(res.data?.data?.actief));
      if (res.data?.data?.verstuurd) setWachtSeconden(60);
    } catch (error) {
      const data = error?.response?.data?.data;
      if (error?.response?.status === 429 && data?.wacht_seconden) {
        setNaar(data.naar || '');
        setWachtSeconden(data.wacht_seconden);
      } else {
        console.error('Verificatiecode aanvragen mislukt:', error);
        setFout('De code versturen lukte niet. Probeer het over een minuut opnieuw.');
      }
    } finally {
      setBezig(false);
    }
  }, []);

  // Pagina geopend zonder geldige verificatie: meteen een code sturen.
  useEffect(() => {
    if (nodig && !geverifieerd && !aangevraagd.current) {
      aangevraagd.current = true;
      vraagCode(true);
    }
  }, [nodig, geverifieerd, vraagCode]);

  // Het codescherm komt op hetzelfde adres in de plaats van een langere pagina (de toestemming, de lijst
  // met analyses): bovenaan beginnen, anders staat de zin dat de code gestuurd is buiten beeld.
  const codeScherm = nodig && !geverifieerd;
  const scherm = useRef(null);
  useEffect(() => {
    if (codeScherm) scherm.current?.scrollIntoView({ block: 'start' });
  }, [codeScherm]);

  useEffect(() => {
    if (wachtSeconden <= 0) return undefined;
    const t = setTimeout(() => setWachtSeconden((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [wachtSeconden]);

  async function controleer(e) {
    e.preventDefault();
    if (bezig || code.length !== 6) return;
    setBezig(true);
    setFout('');
    try {
      const jwt = localStorage.getItem('jwt');
      const res = await axios.post(
        `${STRAPI_URL}/api/video-analyses/verificatie`,
        { code },
        { headers: { Authorization: `Bearer ${jwt}` } }
      );
      bewaarToken(res.data.data.token);
      setCode('');
      setGeverifieerd(true);
    } catch (error) {
      const reden = error?.response?.data?.data?.reden;
      setFout(REDENEN[reden] || 'Controleren lukte niet. Probeer het opnieuw.');
      setCode('');
    } finally {
      setBezig(false);
    }
  }

  if (currentUser?.has_video_analysis && !currentUser?.video_analysis_consent_at) {
    return <VideoAnalyseConsent onConsent={giveVideoAnalysisConsent} />;
  }
  if (!codeScherm) return children;

  return (
    <div ref={scherm} className="page-container video-analyse-page">
      <div className="page-introduction">
        <h1>Video Analyse</h1>
        <p>Video's van lessen zijn gevoelig. Daarom vragen we naast je wachtwoord een code.</p>
      </div>
      <main className="video-analyse-content">
        <div className="consent-card">
          <h2>Voer de code in</h2>
          <p className="consent-intro">
            {naar
              ? `We hebben ${eerderGestuurd ? 'zojuist al ' : ''}een code van 6 cijfers gestuurd naar ${naar}. De code is 10 minuten geldig.`
              : verlopen
                ? 'Je toegang tot de video-analyse in deze browser is verlopen. Druk op "Code sturen". We mailen dan een code van 6 cijfers naar het e-mailadres van je account.'
                : 'We sturen een code van 6 cijfers naar het e-mailadres van je account.'}
          </p>
          <form onSubmit={controleer} className="form-group">
            <label htmlFor="videoVerificatieCode">Code uit de e-mail</label>
            <input
              id="videoVerificatieCode"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              autoFocus
              style={{ fontSize: '1.6rem', letterSpacing: '0.4em', textAlign: 'center', maxWidth: '260px' }}
            />
            {fout && <p className="upload-error" role="alert">{fout}</p>}
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center', marginTop: '1rem' }}>
              <button type="submit" className="btn-primary-action" disabled={bezig || code.length !== 6}>
                {bezig ? 'Even geduld...' : 'Verder'}
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => vraagCode(false)}
                disabled={bezig || wachtSeconden > 0}
              >
                {wachtSeconden > 0
                  ? `Nieuwe code (${wachtSeconden}s)`
                  : verlopen
                    ? 'Code sturen'
                    : 'Nieuwe code sturen'}
              </button>
            </div>
          </form>
          <p className="consent-intro" style={{ marginTop: '1.25rem', marginBottom: 0, fontSize: '0.9rem' }}>
            Geen mail? Kijk in je ongewenste mail. Met de code kun je 8 uur verder in deze browser. Daarna, of als je uitlogt, vragen we een nieuwe code.
          </p>
        </div>
      </main>
    </div>
  );
}

export default VideoVerificatie;
